/**
 * ViewSwitcher.js
 * M-03 §3.2: "Viendo como" selector in header.
 * Lists all role×scope contexts + "Mi vista de empleado".
 */
import React from 'react';
import { Eye, ChevronDown, User, Shield, Building, Building2, MapPin } from 'lucide-react';
import { useView } from '../contexts/ViewContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const SCOPE_ICONS = {
  cliente: Building,
  razon_social: Building2,
  centro: MapPin,
};

export const ViewSwitcher = () => {
  const { language } = useLanguage();
  const {
    contexts,
    activeContext,
    setActiveContext,
    isEmployeeView,
    switchToEmployeeView,
    switchToAdminView,
    hasAdminContexts,
    pendingTasks,
  } = useView();

  // Current label
  const currentLabel = isEmployeeView
    ? (language === 'es' ? 'Mi vista de empleado' : 'My employee view')
    : activeContext
      ? (language === 'es' ? activeContext.label_es : activeContext.label_en)
      : (language === 'es' ? 'Sin contexto' : 'No context');

  const hasPending = pendingTasks.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative gap-2 max-w-[260px]"
          data-testid="view-switcher-trigger"
        >
          <Eye className="h-4 w-4 shrink-0" style={{ color: '#1A878D' }} />
          <span className="truncate text-xs">
            {language === 'es' ? 'Viendo como' : 'Viewing as'}:{' '}
            <span className="font-semibold">{currentLabel}</span>
          </span>
          <ChevronDown className="h-3 w-3 shrink-0" />
          {hasPending && !isEmployeeView && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full animate-pulse" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="view-switcher-menu">
        <DropdownMenuLabel className="text-xs text-gray-500">
          {language === 'es' ? 'Cambiar vista' : 'Switch view'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Admin contexts */}
        {contexts.map((ctx, idx) => {
          const ScopeIcon = SCOPE_ICONS[ctx.scopeType] || Shield;
          const isActive = !isEmployeeView && activeContext?.role === ctx.role
            && activeContext?.scopeType === ctx.scopeType
            && activeContext?.scopeId === ctx.scopeId;
          return (
            <DropdownMenuItem
              key={`${ctx.role}-${ctx.scopeType}-${ctx.scopeId || idx}`}
              onClick={() => setActiveContext(ctx)}
              className={isActive ? 'bg-teal-50 font-medium' : ''}
              data-testid={`view-ctx-${ctx.role}-${ctx.scopeType}`}
            >
              <ScopeIcon className="h-4 w-4 mr-2 shrink-0 text-gray-500" />
              <span className="truncate text-sm">
                {language === 'es' ? ctx.label_es : ctx.label_en}
              </span>
              {isActive && (
                <Badge variant="outline" className="ml-auto text-xs px-1.5 py-0 border-teal-400 text-teal-700">
                  {language === 'es' ? 'Activo' : 'Active'}
                </Badge>
              )}
            </DropdownMenuItem>
          );
        })}

        {/* Employee view option */}
        {hasAdminContexts && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onClick={isEmployeeView ? switchToAdminView : switchToEmployeeView}
          className={isEmployeeView ? 'bg-blue-50 font-medium' : ''}
          data-testid="view-employee-toggle"
        >
          <User className="h-4 w-4 mr-2 text-blue-600" />
          <span className="text-sm">
            {isEmployeeView
              ? (language === 'es' ? 'Volver a vista admin' : 'Back to admin view')
              : (language === 'es' ? 'Mi vista de empleado' : 'My employee view')
            }
          </span>
          {!isEmployeeView && hasPending && (
            <Badge className="ml-auto bg-orange-100 text-orange-700 border-orange-300 text-xs px-1.5 py-0">
              {pendingTasks.length}
            </Badge>
          )}
          {isEmployeeView && (
            <Badge variant="outline" className="ml-auto text-xs px-1.5 py-0 border-blue-400 text-blue-700">
              {language === 'es' ? 'Activo' : 'Active'}
            </Badge>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ViewSwitcher;
